import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { 
  MapPin, User, Phone, MessageCircle, Loader2, CheckCircle, 
  AlertTriangle, Navigation, Send, ExternalLink, Trophy, Star, LogIn
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TAPTAG_LOGO = "https://customer-assets.emergentagent.com/job_726f4ba1-c975-4f39-ba02-ad3131a12031/artifacts/hgye1jst_IMG_0895%20%281%29.PNG";

const ScanPage = () => {
  const { stickerCode } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [stickerInfo, setStickerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rewardResult, setRewardResult] = useState(null);
  const [rewardClaimed, setRewardClaimed] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: '',
    shareLocation: false
  });

  useEffect(() => {
    fetchStickerInfo();
  }, [stickerCode]);

  // Auto-claim reward when user is logged in and sticker is loaded
  useEffect(() => {
    if (!authLoading && user && stickerInfo && !rewardClaimed) {
      claimReward();
    }
  }, [authLoading, user, stickerInfo, rewardClaimed]);

  const claimReward = async () => {
    setRewardClaimed(true);
    try {
      const rewardRes = await axios.post(`${API_URL}/api/rewards/claim-scan?sticker_code=${stickerCode}`);
      setRewardResult(rewardRes.data);
      if (rewardRes.data.leveled_up) {
        toast.success(`Urime! Arrite nivelin ${rewardRes.data.new_level}: ${rewardRes.data.level_name}!`);
      } else {
        toast.success(`Fitove +${rewardRes.data.points_earned} pikë!`);
      }
    } catch (rewardErr) {
      // Own item, already scanned, or not linked - that's ok
      console.log('Reward claim:', rewardErr.response?.data?.detail);
    }
  };

  const fetchStickerInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scan/${stickerCode}`);
      setStickerInfo(response.data);
      
      // Record the scan
      await axios.post(`${API_URL}/api/scan/${stickerCode}/record`, {
        latitude: null,
        longitude: null
      });
    } catch (err) {
      setError('Ky stiker nuk u gjet ose nuk është aktiv.');
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setFormData(prev => ({ ...prev, shareLocation: true }));
          setLocationLoading(false);
          toast.success('Lokacioni u kap me sukses!');
        },
        (err) => {
          console.error('Error getting location:', err);
          toast.error('Nuk mund të kapet lokacioni');
          setLocationLoading(false);
        },
        { enableHighAccuracy: true }
      );
    } else {
      toast.error('Shfletuesi nuk e mbështet lokacionin');
      setLocationLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      toast.error('Plotëso emrin dhe numrin e telefonit');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/scan/${stickerCode}/contact`, {
        message: formData.message || 'Kam gjetur objektin tuaj!',
        sender_name: formData.name,
        sender_phone: formData.phone,
        latitude: formData.shareLocation ? location?.latitude : null,
        longitude: formData.shareLocation ? location?.longitude : null,
        scanner_user_id: user?.id || null
      });
      
      setMessageSent(true);
      toast.success('Mesazhi u dërgua te pronari!');
    } catch (err) {
      toast.error('Gabim në dërgimin e mesazhit');
    } finally {
      setSubmitting(false);
    }
  };

  const getGoogleMapsLink = () => {
    if (location) {
      return `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-violet-600 mx-auto mb-4" />
          <p className="text-slate-600">Duke ngarkuar...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">Stikeri Nuk u Gjet</h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-violet-600 hover:bg-violet-700 text-white">
                Kthehu në Faqen Kryesore
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <img 
            src={TAPTAG_LOGO}
            alt="TAPTAG"
            className="w-10 h-10 rounded-full object-cover shadow"
          />
          <div>
            <h1 className="font-bold text-slate-900">TapTag</h1>
            <p className="text-xs text-slate-500">Sistemi i Gjetjes së Objekteve</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-8">
        {messageSent ? (
          /* Success State */
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Faleminderit!</h2>
              <p className="text-slate-600 mb-6">
                Mesazhi juaj u dërgua te pronari. Ai/ajo do t'ju kontaktojë së shpejti.
              </p>
              {location && (
                <a 
                  href={getGoogleMapsLink()} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700"
                >
                  <MapPin className="w-4 h-4" />
                  Shiko lokacionin në Google Maps
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              
              {/* Reward Points Info */}
              {rewardResult && (
                <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Trophy className="w-5 h-5 text-amber-600" />
                    <span className="font-bold text-amber-800">+{rewardResult.points_earned} pikë!</span>
                  </div>
                  <p className="text-sm text-amber-700">
                    Totali: {rewardResult.total_points} pikë | Niveli {rewardResult.new_level}: {rewardResult.level_name}
                  </p>
                  {rewardResult.reward && (
                    <div className="mt-2 p-2 bg-white rounded-lg border border-amber-300">
                      <p className="text-sm font-semibold text-amber-800 flex items-center justify-center gap-1">
                        <Star className="w-4 h-4" />
                        {rewardResult.reward.message}
                      </p>
                    </div>
                  )}
                  <Link to="/profile" className="inline-block mt-3 text-sm text-violet-600 hover:text-violet-700 underline">
                    Shiko profilin e shpërblimeve
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Lost Item Info */}
            <Card className="border-0 shadow-xl mb-6 overflow-hidden">
              {stickerInfo?.is_lost && (
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">Ky objekt është shënuar si i humbur</span>
                </div>
              )}
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-8 h-8 text-violet-600" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900">Keni Gjetur një Objekt!</h2>
                </div>

                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-sm text-slate-500 mb-1">Objekti</p>
                    <p className="font-semibold text-slate-900">{stickerInfo?.object_name}</p>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-sm text-slate-500 mb-1">Pronari</p>
                    <p className="font-semibold text-slate-900 flex items-center gap-2">
                      <User className="w-4 h-4 text-violet-600" />
                      {stickerInfo?.owner_name}
                    </p>
                  </div>

                  <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
                    <p className="text-sm text-violet-600 mb-1">Mesazhi nga pronari</p>
                    <p className="text-slate-800">{stickerInfo?.message}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reward Points Notification (shown automatically for logged-in users) */}
            {rewardResult && (
              <Card className="border-0 shadow-xl mb-6 overflow-hidden">
                <div className="bg-gradient-to-r from-amber-400 to-orange-400 p-4">
                  <div className="flex items-center justify-center gap-2 text-white">
                    <Trophy className="w-6 h-6" />
                    <span className="font-bold text-lg">+{rewardResult.points_earned} pikë!</span>
                  </div>
                </div>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-slate-600">
                    Totali: {rewardResult.total_points} pikë | Niveli {rewardResult.new_level}: {rewardResult.level_name}
                  </p>
                  {rewardResult.reward && (
                    <div className="mt-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm font-semibold text-amber-800 flex items-center justify-center gap-1">
                        <Star className="w-4 h-4" />
                        {rewardResult.reward.message}
                      </p>
                    </div>
                  )}
                  <Link to="/profile" className="inline-block mt-2 text-sm text-violet-600 hover:text-violet-700 underline">
                    Shiko profilin e shpërblimeve
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Login to earn points (for non-logged-in users) */}
            {!authLoading && !user && (
              <Card className="border-0 shadow-xl mb-6 overflow-hidden border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Trophy className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900 text-sm">Fito pikë për këtë skanim!</p>
                      <p className="text-xs text-slate-500">Hyr në llogarinë tënde për të mbledhur pikë dhe shpërblime</p>
                    </div>
                    <Link to={`/login?redirect=/scan/${stickerCode}`}>
                      <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="scan-login-btn">
                        <LogIn className="w-4 h-4 mr-1" />
                        Hyr
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Form */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-violet-600" />
                  Kontakto Pronarin
                </h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-slate-700">Emri juaj *</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Emri dhe mbiemri"
                      className="mt-1"
                      data-testid="finder-name"
                    />
                  </div>

                  <div>
                    <Label className="text-slate-700">Numri i telefonit *</Label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+383 4X XXX XXX"
                      className="mt-1"
                      data-testid="finder-phone"
                    />
                  </div>

                  <div>
                    <Label className="text-slate-700">Mesazhi (opsional)</Label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="P.sh. E kam gjetur në..."
                      className="mt-1 min-h-[80px]"
                      data-testid="finder-message"
                    />
                  </div>

                  {/* Location Section */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium text-slate-900">Ndaj Lokacionin</p>
                        <p className="text-xs text-slate-500">Ndihmon pronarin të gjejë objektin</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={getLocation}
                        disabled={locationLoading || location}
                        className={location ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : ''}
                        data-testid="share-location-btn"
                      >
                        {locationLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : location ? (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            I kapur
                          </>
                        ) : (
                          <>
                            <Navigation className="w-4 h-4 mr-1" />
                            Kap Lokacionin
                          </>
                        )}
                      </Button>
                    </div>
                    {location && (
                      <div className="text-sm text-slate-600 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-emerald-600" />
                        Lokacioni: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                        <a 
                          href={getGoogleMapsLink()} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-violet-600 hover:underline flex items-center gap-1"
                        >
                          Hap në Maps <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-semibold"
                    data-testid="send-message-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Duke dërguar...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Dërgo Mesazhin
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default ScanPage;
